<script setup lang="ts">
import { computed, ref, type ComputedRef } from 'vue'
import { storeToRefs } from 'pinia'
import PopupMenu from '@/components/PopupMenu.vue'
import type { MenuItemType } from '@/components/dropdown-menu/types'
import { useLibraryStore } from '@/library/library.store'
import { getFileIconSrc } from '@/library/utils/fileIcons'
import { timeAgo } from '@/util/time'
import FolderAvatar from '@/library/FolderAvatar.vue'
import { useLibraryFolderAnalytics } from '@/library/composables/useLibraryFolderAnalytics'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { generateAttachmentDownloadUrl } from '@/library/api'
import DeleteAttachmentModal from '@/library/components/DeleteAttachmentModal.vue'
import { ATTACHMENT_TYPE, OPEN_METHOD } from '@/library/utils/urlUtils'
import FileDownloadIcon from '@/components/FileDownloadIcon.vue'

const props = defineProps<{
  attachment: any
}>()

const emit = defineEmits<{
  error: [payload: { message: string; error: unknown }]
  deleted: []
  edit: []
}>()

const isDownloading = ref(false)
const showDeleteModal = ref(false)

const libraryStore = useLibraryStore()
const {
  currentFolder,
  getContributorIdsForContent,
  canDeleteCurrentFolderContent,
  canEditCurrentFolderContent,
} = storeToRefs(libraryStore)

const { getReactiveEventAttributes } = useLibraryFolderAnalytics()
const analytics = getAnalyticsTracker().getChildContext(getReactiveEventAttributes())

const fileExtension = computed((): string => props.attachment?.content_body?.file_extension || '')

const fileIconSrc = computed((): string => getFileIconSrc(fileExtension.value))

const displayTitle = computed((): string => props.attachment?.content_title?.trim() || '')

const timeText = computed((): string => {
  return props.attachment?.updated_at ? timeAgo(props.attachment.updated_at) : ''
})

const bylineText = computed((): string => {
  const ext = fileExtension.value ? `.${fileExtension.value}` : ''
  return [ext, timeText.value].filter(Boolean).join(' - ')
})

const menuTriggerAriaLabel = computed((): string => {
  const title = displayTitle.value
  if (!title) return 'File attachment options'
  return `File attachment options for ${title}`
})

const contentId = computed((): string => props.attachment?.content_uuid || '')

type ContributorInfo = { contributor: string; isFolderOwner: boolean }

const contributorInfo: ComputedRef<ContributorInfo | undefined> = computed(() => {
  return getContributorIdsForContent.value?.[contentId.value]
})

const contributorUUID: ComputedRef<string> = computed(() => contributorInfo.value?.contributor || '')

const shouldShowContributorByline: ComputedRef<boolean> = computed(() => {
  return !!(contributorUUID.value && !contributorInfo.value?.isFolderOwner)
})

const handleDownload = async (openMethod: string) => {
  if (isDownloading.value) return
  isDownloading.value = true
  try {
    const folderUuid = props.attachment?.collection_uuid || ''
    const { url } = await generateAttachmentDownloadUrl(contentId.value, folderUuid)
    analytics.sendEvent(EVENT_NAMES.LIBRARY.ATTACHMENT.OPENED_LIBRARY_ATTACHMENT, {
      attachment_type: ATTACHMENT_TYPE.FILE,
      attachment_uuid: contentId.value,
      open_method: openMethod,
      file_type: fileExtension
    })
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    a.click()
  } catch (e) {
    console.error('Failed to generate download URL', e)
    const details = e instanceof Error ? e.message : String(e)
    emit('error', { message: `Failed to download file: ${details}`, error: e })
  } finally {
    isDownloading.value = false
  }
}

const handleDelete = (): void => {
  showDeleteModal.value = true
}

const handleEdit = (): void => {
  emit('edit')
}

const menuItems = computed((): MenuItemType[] => [
  {
    label: 'Download',
    srLabel: 'opens in a new tab',
    icon: FileDownloadIcon,
    action: () => { handleDownload(OPEN_METHOD.MENU_OPEN) },
  },
  ...(canEditCurrentFolderContent.value ? [
    {
      label: 'Edit',
      action: handleEdit,
    },
  ] : []),
  ...(canDeleteCurrentFolderContent.value ? [
    {
      label: 'Delete',
      action: handleDelete,
    },
  ] : []),
])
</script>

<template>
  <div
    class="relative flex w-link-card cursor-pointer flex-col overflow-hidden rounded-xs bg-gray-50 px-2 py-3 shadow-lib-card hover:shadow-lib-card-hover transition-shadow"
    role="button"
    tabindex="0"
    :aria-label="displayTitle ? `${displayTitle} (download file, opens in a new tab)` : 'Download file (opens in a new tab)'"
    @click="handleDownload(OPEN_METHOD.CARD_CLICK)"
    @keydown.enter.prevent="handleDownload(OPEN_METHOD.CARD_CLICK)"
    @keydown.space.prevent="handleDownload(OPEN_METHOD.CARD_CLICK)"
  >
    <div class="flex items-start justify-between">
      <div class="flex min-w-0 items-start gap-1.5">
        <div class="h-6 w-6 shrink-0">
          <img
            :src="fileIconSrc"
            alt=""
            class="block h-6 w-6 max-w-none"
            aria-hidden="true"
          />
        </div>

        <div class="min-w-0 flex-1">
          <div class="min-w-0">
            <div class="truncate text-md font-medium leading-5 text-toolkit-blue-hover-link">
              {{ displayTitle }}
            </div>
            <div
              v-if="bylineText"
              class="-mt-0.5 truncate text-sm leading-link-card-subtitle text-gray-700"
            >
              {{ bylineText }}
            </div>
          </div>
        </div>
      </div>

      <div class="shrink-0" @click.stop @keydown.stop>
        <PopupMenu
          :items="menuItems"
          :triggerAriaLabel="menuTriggerAriaLabel"
          triggerClass="flex items-center justify-center h-6 w-6 rounded-full text-gray-900 hover:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-600 focus-visible:bg-gray-300"
          triggerIconClass="text-[20px]"
        />
      </div>
    </div>

    <div
      v-if="shouldShowContributorByline && contributorUUID"
      class="flex items-center gap-1 pl-link-card-indent text-sm leading-link-card-subtitle text-gray-700"
    >
      <span>Contributed by</span>
      <FolderAvatar
        :userId="contributorUUID"
        :folder="currentFolder"
        avatarSize="xs"
      />
    </div>

  </div>

  <DeleteAttachmentModal
    v-model:externalOpen="showDeleteModal"
    :contentUuid="contentId"
    :folderUuid="props.attachment?.collection_uuid || ''"
    :attachmentName="displayTitle"
    @deleted="emit('deleted')"
  />
</template>
